import React, { useEffect, useState } from "react";

import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  LuCalendarClock,
  LuDoorClosed,
  LuDoorOpen,
  LuPower,
  LuPowerOff,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getAccessControlEnabled,
  getAccessWindows,
  setAccessControl,
} from "../../features/appSettings/appSettingsSlice";
import { PUT } from "../../services/axios.service";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { AccessWindowProps } from "../../types/settings";

interface IFormInput {
  accessControlEnabled: boolean;
  accessWindows: AccessWindowProps[];
}

const weekDayLabels = [
  "રવિવાર",
  "સોમવાર",
  "મંગળવાર",
  "બુધવાર",
  "ગુરુવાર",
  "શુક્રવાર",
  "શનિવાર",
];

const accessTimeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;

const accessControlEnabledOptions = [
  { value: true, label: "હા ચાલુ કરો" },
  { value: false, label: "ના બંધ રાખો" },
];

const dayOpenOptions = [
  { value: true, label: "ખુલ્લું" },
  { value: false, label: "બંધ" },
];

const buildDefaultWindows = (): AccessWindowProps[] =>
  Array.from({ length: 7 }, (_, day) => ({
    day,
    isOpen: false,
    startTime: "",
    endTime: "",
  }));

const mergeWindows = (windows: AccessWindowProps[]): AccessWindowProps[] =>
  buildDefaultWindows().map((defaultWindow) => {
    const found = windows?.find((window) => window.day === defaultWindow.day);

    return found
      ? {
          day: defaultWindow.day,
          isOpen: !!found.isOpen,
          startTime: found.startTime ?? "",
          endTime: found.endTime ?? "",
        }
      : defaultWindow;
  });

const toMinutes = (time: string | null) => {
  if (!time) return null;

  const [hours, minutes] = time.split(":").map(Number);

  return hours * 60 + minutes;
};

const AccessControlForm = () => {
  const dispatch = useAppDispatch();
  const accessControlEnabled = useAppSelector(getAccessControlEnabled);
  const accessWindows = useAppSelector(getAccessWindows);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    mode: "onSubmit",
    defaultValues: {
      accessControlEnabled: false,
      accessWindows: buildDefaultWindows(),
    },
  });

  const { fields } = useFieldArray({ control, name: "accessWindows" });

  const watchEnabled = watch("accessControlEnabled");

  useEffect(() => {
    reset({
      accessControlEnabled,
      accessWindows: mergeWindows(accessWindows),
    });
  }, [accessControlEnabled, accessWindows, reset]);

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);

    try {
      await PUT("/v1/admin/settings/access-control", {
        accessControlEnabled: data.accessControlEnabled,
        accessWindows: data.accessWindows.map((window: AccessWindowProps) => ({
          day: window.day,
          isOpen: window.isOpen,
          startTime: window.isOpen ? window.startTime || null : null,
          endTime: window.isOpen ? window.endTime || null : null,
        })),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            dispatch(
              setAccessControl({
                accessControlEnabled:
                  !!response.data.data.accessControlEnabled,
                accessWindows: response.data.data.accessWindows || [],
              })
            );

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight flex items-center gap-2">
          <LuCalendarClock className="h-6 w-6 text-[#34699A]" />
          સિસ્ટમ એક્સેસ ટાઈમ
        </div>
        <div className="text-sm text-gray-600">
          જ્યારે ચાલુ હોય, ત્યારે Super Admin સિવાયના બધા યુઝર્સ ફક્ત નીચે નક્કી
          કરેલા દિવસ અને સમય દરમિયાન જ સિસ્ટમ વાપરી શકશે. સમય બહાર કંઈ પણ કરવા
          જતાં તેઓ આપોઆપ લોગઆઉટ થઈ જશે. (સમય: IST)
        </div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="space-y-2 max-w-xs">
            <label className="text-sm font-semibold text-[#113F67]">
              એક્સેસ ટાઈમ કંટ્રોલ <span className="text-red-500">*</span>
            </label>

            <div className="relative">
              {watchEnabled ? (
                <LuPower className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
              ) : (
                <LuPowerOff className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
              )}
              <Controller
                control={control}
                name="accessControlEnabled"
                render={({ field }) => (
                  <ReactSelect
                    value={
                      accessControlEnabledOptions.find(
                        (option) => option.value === field.value
                      ) || null
                    }
                    options={accessControlEnabledOptions}
                    placeholder="પસંદ કરો"
                    isSearchable={false}
                    styles={iconSelectStyles as any}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    onChange={(option: any) => field.onChange(option?.value)}
                  />
                )}
              />
            </div>
          </div>

          <div
            className={`space-y-3 ${
              watchEnabled ? "" : "opacity-60 pointer-events-none"
            }`}
          >
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <div className="sm:col-span-3">દિવસ</div>
              <div className="sm:col-span-3">સ્થિતિ</div>
              <div className="sm:col-span-3">શરૂઆતનો સમય</div>
              <div className="sm:col-span-3">સમાપ્તિનો સમય</div>
            </div>

            {fields.map((row, index) => {
              const isOpen = watch(`accessWindows.${index}.isOpen`);
              const rowError = errors.accessWindows?.[index];

              return (
                <div
                  key={row.id}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:items-center border border-gray-100 rounded-lg p-3"
                >
                  <div className="sm:col-span-3 font-semibold text-[#113F67]">
                    {weekDayLabels[row.day]}
                  </div>

                  <div className="sm:col-span-3">
                    <div className="relative">
                      {isOpen ? (
                        <LuDoorOpen className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
                      ) : (
                        <LuDoorClosed className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
                      )}
                      <Controller
                        control={control}
                        name={`accessWindows.${index}.isOpen`}
                        render={({ field }) => (
                          <ReactSelect
                            value={
                              dayOpenOptions.find(
                                (option) => option.value === field.value
                              ) || null
                            }
                            options={dayOpenOptions}
                            isSearchable={false}
                            styles={iconSelectStyles as any}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            onChange={(option: any) =>
                              field.onChange(option?.value)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <Controller
                      control={control}
                      name={`accessWindows.${index}.startTime`}
                      rules={{
                        validate: (value, formValues) =>
                          !formValues.accessWindows[index].isOpen ||
                          accessTimeRegex.test(value || "") ||
                          "માન્ય શરૂઆતનો સમય દાખલ કરો.",
                      }}
                      render={({ field }) => (
                        <input
                          type="time"
                          disabled={!isOpen}
                          value={field.value || ""}
                          onChange={(event) => field.onChange(event.target.value)}
                          className="flex w-full bg-white px-3 py-2 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 h-11 rounded-md border border-[hsl(214_32%_91%)] text-base"
                        />
                      )}
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <Controller
                      control={control}
                      name={`accessWindows.${index}.endTime`}
                      rules={{
                        validate: (value, formValues) => {
                          const window = formValues.accessWindows[index];

                          if (!window.isOpen) return true;

                          if (!accessTimeRegex.test(value || ""))
                            return "માન્ય સમાપ્તિનો સમય દાખલ કરો.";

                          const start = toMinutes(window.startTime);
                          const end = toMinutes(value);

                          if (start === null || end === null) return true;

                          return (
                            end > start ||
                            "સમાપ્તિ સમય શરૂઆતના સમય કરતાં પછીનો હોવો જોઈએ."
                          );
                        },
                      }}
                      render={({ field }) => (
                        <input
                          type="time"
                          disabled={!isOpen}
                          value={field.value || ""}
                          onChange={(event) => field.onChange(event.target.value)}
                          className="flex w-full bg-white px-3 py-2 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 h-11 rounded-md border border-[hsl(214_32%_91%)] text-base"
                        />
                      )}
                    />
                  </div>

                  {(rowError?.startTime || rowError?.endTime) && (
                    <p className="sm:col-span-12 text-red-500 text-sm">
                      {rowError?.startTime?.message || rowError?.endTime?.message}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  સેવ થઈ રહ્યું છે...
                </div>
              ) : (
                "સેવ કરો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default AccessControlForm;
